import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import GrowthChart from './GrowthChart';
import SocialProof from './SocialProof';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiInfo, FiDollarSign, FiCalendar, FiUser, FiArrowRight, FiDownload } = FiIcons;

const Calculator = ({ onResultsUpdate }) => {
  const [formData, setFormData] = useState({ salary: '', age: '', years: '' });
  const [results, setResults] = useState(null);

  const calculate = () => {
    const salary = parseFloat(formData.salary);
    const age = parseInt(formData.age);
    const years = parseInt(formData.years);
    const dividendRate = 0.055;

    if (!salary || !years || isNaN(salary) || isNaN(years)) return;

    const monthlyContribution = salary * 0.24;
    const annualContribution = monthlyContribution * 12;
    let currentBalance = 0;
    const chartData = [{ year: 0, total: 0 }];

    for (let i = 1; i <= years; i++) {
      currentBalance = (currentBalance + annualContribution) * (1 + dividendRate);
      chartData.push({ year: i, total: Math.round(currentBalance) });
    }

    const calculatedResults = {
      inputs: { salary, age, years },
      monthly: monthlyContribution,
      annual: annualContribution,
      total: currentBalance,
      chartData: chartData
    };

    setResults(calculatedResults);
    if (onResultsUpdate) {
      onResultsUpdate(calculatedResults);
    }
  };

  const handleCalculate = (e) => {
    e.preventDefault();
    calculate();
  };

  const scrollToEmail = () => {
    const element = document.getElementById('email-capture');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const formatRM = (val) => new Intl.NumberFormat('en-MY', { 
    style: 'currency', 
    currency: 'MYR', 
    minimumFractionDigits: 0 
  }).format(val);

  return (
    <section id="calculator" className="py-24 bg-slate-50 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <SocialProof />
          <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight">
            Comprehensive EPF Estimator
          </h2>
          <p className="mt-4 text-slate-600 max-w-2xl mx-auto text-lg">
            Our algorithm uses historical dividend averages and standard contribution rates to project your financial future.
          </p>
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white rounded-[2.5rem] shadow-2xl shadow-blue-900/5 border border-slate-100 overflow-hidden"
        >
          <div className="grid grid-cols-1 lg:grid-cols-12">
            {/* Input Form */}
            <div className="p-8 md:p-12 lg:col-span-4 bg-slate-50/50 border-r border-slate-100">
              <form onSubmit={handleCalculate} className="space-y-8">
                <div>
                  <label className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-3 uppercase tracking-wider">
                    <SafeIcon icon={FiDollarSign} className="text-brand-600" /> Monthly Basic Salary
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold">RM</span>
                    <input 
                      type="number" required placeholder="5,000" 
                      className="input-field pl-14 text-lg font-bold" 
                      value={formData.salary} 
                      onChange={(e) => setFormData({ ...formData, salary: e.target.value })} 
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="flex items-center gap-2 text-xs font-bold text-slate-700 mb-3 uppercase tracking-wider">
                      <SafeIcon icon={FiUser} className="text-brand-600" /> Current Age
                    </label>
                    <input 
                      type="number" required placeholder="30" 
                      className="input-field font-bold" 
                      value={formData.age} 
                      onChange={(e) => setFormData({ ...formData, age: e.target.value })} 
                    />
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-xs font-bold text-slate-700 mb-3 uppercase tracking-wider">
                      <SafeIcon icon={FiCalendar} className="text-brand-600" /> Target Years
                    </label>
                    <input 
                      type="number" required placeholder="25" 
                      className="input-field font-bold" 
                      value={formData.years} 
                      onChange={(e) => setFormData({ ...formData, years: e.target.value })} 
                    />
                  </div>
                </div>

                <button type="submit" className="w-full btn-primary py-5 text-xl shadow-xl shadow-brand-600/30 font-bold">
                  Generate Projection
                </button>
              </form>

              <div className="mt-10 p-6 bg-white rounded-2xl border border-slate-200/60 shadow-sm">
                <div className="flex gap-4">
                  <SafeIcon icon={FiInfo} className="w-5 h-5 text-brand-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-slate-500 leading-relaxed font-medium">
                    Calculated at 11% employee + 13% employer rate with an estimated 5.5% annual dividend.
                  </p>
                </div>
              </div>
            </div>

            {/* Results & Chart */}
            <div className="p-8 md:p-12 lg:col-span-8 bg-white min-h-[600px] flex flex-col">
              <AnimatePresence mode="wait">
                {!results ? (
                  <motion.div 
                    key="empty" 
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="flex-grow flex flex-col items-center justify-center text-center space-y-6"
                  >
                    <div className="w-24 h-24 bg-slate-50 rounded-[2rem] flex items-center justify-center text-slate-300 transform rotate-12 border border-slate-100">
                      <SafeIcon icon={FiTrendingUp} className="w-12 h-12" />
                    </div>
                    <div>
                      <h4 className="text-2xl font-black text-slate-900">Visualize Your Future</h4>
                      <p className="text-slate-500 mt-2 max-w-xs mx-auto text-lg">
                        Enter your financial details to see your retirement nest egg growth.
                      </p>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div 
                    key="results" 
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    className="flex flex-col h-full"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                      <div>
                        <p className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-3">Projected Total Savings</p>
                        <h2 className="text-5xl md:text-6xl font-black text-slate-900 tracking-tight">
                          {formatRM(results.total)}
                        </h2>
                      </div>
                      <div className="flex flex-col justify-end">
                        <div className="flex gap-10 border-l-4 border-brand-100 pl-8">
                          <div>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Monthly</p>
                            <p className="text-2xl font-bold text-brand-600">{formatRM(results.monthly)}</p>
                          </div>
                          <div>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Annual</p>
                            <p className="text-2xl font-bold text-brand-600">{formatRM(results.annual)}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex-grow bg-slate-50/50 rounded-[2rem] p-8 border border-slate-100 relative mb-12">
                      <GrowthChart data={results.chartData} />
                    </div>

                    {/* High-Converting CTA Block */}
                    <div className="border-t border-slate-100 pt-10">
                      <div className="flex flex-col md:flex-row items-center justify-between gap-6 bg-brand-50/50 p-8 rounded-3xl border border-brand-100">
                        <div className="text-center md:text-left">
                          <h4 className="text-xl font-bold text-slate-900 mb-1">Want a detailed breakdown?</h4>
                          <p className="text-sm text-slate-600 font-medium">
                            Get a full report including growth insights and retirement scenarios.
                          </p>
                        </div>
                        <div className="flex flex-col items-center">
                          <button 
                            onClick={scrollToEmail}
                            className="btn-primary group px-8 py-4 whitespace-nowrap flex items-center gap-2"
                          >
                            <SafeIcon icon={FiDownload} className="w-5 h-5" />
                            Download Full EPF Report
                          </button>
                          <span className="mt-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                            Official Summary
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Calculator;